import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as dates from 'react-schedule-calendar/lib/utils/dates';
import { Typography, Chip, Grid2 as Grid, IconButton } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { navigate } from 'react-schedule-calendar/lib/utils/constants';
import * as selectors from '../../../store/selectors';
import EventTime from './EventTime';
import EventDetails from './EventDetails';
import { MoreVert } from '@mui/icons-material';
import StaffAllocationDialog from './StaffAllocationDialog';

import * as usersActions from '../../../store/users.actions';
import * as eventsActions from '../../../store/events.actions';
import TooltipWrapper from '../../TooltipWrapper';
import { sortEvents, inRange } from '../../../helpers/dates';

const styles = theme => ({
  viewContainer: {
    overflowY: 'auto',
  },
  cellTitle: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  eventsTableCell: {
    padding: 0,
  },
  eventDetails: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    flexWrap: 'wrap',
    [`${theme.breakpoints.down('lg')}`]: {
      flexDirection: 'column-reverse',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: 0,
      paddingRight: 0,
    },
    '&:hover': {
      cursor: 'pointer',
    },
  },
  noBorderBottom: {
    borderBottom: 'none',
  },
  noPadding: {
    padding: 0,
  },
  staffAllocationCell: {
    paddingLeft: theme.spacing(3),
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    [`${theme.breakpoints.down('lg')}`]: {
      paddingLeft: 0,
      paddingTop: theme.spacing(0.5),
    },
  },
  staffAllocationWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    [`${theme.breakpoints.down('lg')}`]: {
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
    },
    [`${theme.breakpoints.down('md')}`]: {
      alignItems: 'center',
    },
  },
  userChip: {
    marginBottom: theme.spacing(0.5),
    [`${theme.breakpoints.down('lg')}`]: {
      marginRight: 0,
      fontSize: theme.spacing(1.25),
      height: theme.spacing(2.5),
    },
    marginRight: theme.spacing(2),
  },
  dateCell: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
    [`${theme.breakpoints.down('lg')}`]: {
      paddingRight: theme.spacing(1),
    },
  },
  flightDetailsCell: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  withBorderBottom: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
});

class StaffAllocationView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isStaffAllocationDialogOpen: false,
      currentEvent: null,
      events: props.events,
    };
  }

  componentDidMount() {
    const { getUsers } = this.props;
    getUsers();
  }

  handleStaffAllocationDialogOpen = currentEvent => {
    this.setState({
      isStaffAllocationDialogOpen: true,
      currentEvent: currentEvent,
    });
  };

  handleStaffAllocationDialogClose = () => {
    this.setState({
      isStaffAllocationDialogOpen: false,
      currentEvent: null,
    });
  };

  handleStaffAllocationFormSubmit = values => {
    const { updateEvent } = this.props;
    const { currentEvent } = this.state;
    updateEvent(currentEvent._id, {
      allocatedStaff: values.allocatedStaff,
    });
    this.handleStaffAllocationDialogClose();
  };

  handleAllocatedStaffDelete = (oldAllocation, event) => {
    const { updateEvent } = this.props;
    updateEvent(event._id, {
      allocatedStaff: event.allocatedStaff.filter(
        allocation => oldAllocation.user._id !== allocation.user._id
      ),
    });
  };

  render() {
    const {
      classes,
      events,
      date,
      accessors,
      localizer,
      airports,
      users,
      onSelectEvent,
    } = this.props;

    const { isStaffAllocationDialogOpen, currentEvent } = this.state;

    const end = dates.add(date, 1, 'month');
    const days = dates.range(date, end, 'day');

    const eventsInRange = events
      .sort((a, b) => sortEvents(a, b, accessors))
      .filter(event => inRange(event, date, end, accessors));

    return (
      <div className={classes.viewContainer}>
        <Grid container className={classes.withBorderBottom}>
          <Grid className={classes.dateCell} size={{ xs: 2, md: 1 }}>
            <Typography className={classes.cellTitle} variant="caption">
              Date
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, md: 11 }} container>
            <Grid size={{ xs: 5, md: 3 }} className={classes.flightDetailsCell}>
              <Typography className={classes.cellTitle} variant="caption">
                Flight details
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 7, md: 9 }}
              className={classes.staffAllocationCell}
            >
              <Typography className={classes.cellTitle} variant="caption">
                Staff Assigned
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {days.map(day => {
          const eventsInDay = eventsInRange.filter(event =>
            inRange(
              event,
              dates.startOf(day, 'day'),
              dates.endOf(day, 'day'),
              accessors
            )
          );

          return (
            <Grid className={classes.withBorderBottom} container key={day}>
              <Grid className={classes.dateCell} size={{ xs: 2, md: 1 }}>
                <Typography variant="caption">
                  {localizer.format(day, 'agendaDateFormat')}
                </Typography>
              </Grid>
              <Grid
                container
                size={{ xs: 10, md: 11 }}
                className={classes.noPadding}
              >
                <Grid container size={{ xs: 12 }}>
                  {eventsInDay.map((event, index) => (
                    <Grid
                      className={
                        index === eventsInDay.length - 1
                          ? null
                          : classes.withBorderBottom
                      }
                      container
                      size={{ xs: 12 }}
                      key={event._id}
                    >
                      <Grid size={{ xs: 5, md: 3 }}>
                        <div
                          onClick={() => onSelectEvent(event)}
                          style={{
                            borderLeft: `3px solid ${
                              airports.find(
                                airport => airport.name === event.airport
                              ).color
                            }`,
                          }}
                          className={classes.eventDetails}
                        >
                          <EventTime day={day} event={event} />
                          <EventDetails event={event} />
                        </div>
                      </Grid>
                      <Grid
                        container
                        justifyContent="space-between"
                        size={{ xs: 7, md: 9 }}
                        className={classes.staffAllocationCell}
                      >
                        <Grid size={{ xs: 9, md: 11 }}>
                          <div className={classes.staffAllocationWrapper}>
                            {event.allocatedStaff
                              ? event.allocatedStaff.map(allocation => {
                                  return (
                                    <TooltipWrapper
                                      title={`${allocation.user.firstName} ${allocation.user.lastName}`}
                                    >
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        key={allocation.user._id}
                                        label={`${allocation.user.firstName[0]}. ${allocation.user.lastName}`}
                                        onDelete={() =>
                                          this.handleAllocatedStaffDelete(
                                            allocation,
                                            event
                                          )
                                        }
                                        color="primary"
                                        className={classes.userChip}
                                      />
                                    </TooltipWrapper>
                                  );
                                })
                              : null}
                          </div>
                        </Grid>

                        <Grid
                          container
                          justifyContent="center"
                          size={{
                            xs: 3,
                            md: 1,
                          }}
                        >
                          <IconButton
                            onClick={() =>
                              this.handleStaffAllocationDialogOpen(event)
                            }
                            color="primary"
                            size="small"
                          >
                            <MoreVert />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        {currentEvent && (
          <StaffAllocationDialog
            users={users}
            event={currentEvent}
            isOpen={isStaffAllocationDialogOpen}
            onClose={this.handleStaffAllocationDialogClose}
            onSubmit={this.handleStaffAllocationFormSubmit}
          />
        )}
      </div>
    );
  }
}

StaffAllocationView.range = date => {
  return {
    start: dates.startOf(date, 'month'),
    end: dates.add(date, 1, 'month'),
  };
};

StaffAllocationView.title = (date, { localizer }) => {
  return localizer.format(
    {
      start: dates.startOf(date, 'day'),
      end: dates.add(date, 1, 'month'),
    },
    'agendaHeaderFormat'
  );
};

StaffAllocationView.navigate = (date, action) => {
  switch (action) {
    case navigate.PREVIOUS:
      return dates.add(date, -1, 'month');

    case navigate.NEXT:
      return dates.add(date, 1, 'month');

    default:
      return date;
  }
};

export default connect(
  state => ({
    airports: selectors.getAirports(state),
    users: selectors.getUsers(state),
    events: selectors.getFilteredEvents(state),
  }),
  {
    getUsers: usersActions.getUsers,
    updateEvent: eventsActions.updateEvent,
  }
)(withStyles(styles)(StaffAllocationView));
